import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import enGbLocale from '@angular/common/locales/en-GB';
import nbLocale from '@angular/common/locales/nb';
import { LOCALE_ID, enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { loadTranslations } from '@angular/localize';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import {
  CallCorrelationIdHeaderName,
  ORG_ID,
  SessionCorrelationIdHeaderName,
  SessionDto,
  vismaCallId,
  vismaSessionId,
} from '@data-access/bulk-operations-api';
import {
  getAngularFileLocaleName,
  getLocalePreference,
  getPathWithPreferredOrgId,
  getPreferredOdpOrgId,
  getVersionFromBaseUri,
  getVersionFromUrl,
} from '@em-helpers';

import { AppModule } from './app/app.module';
import { USER_SESSION } from './app/core/core.module';
import { environment } from './environments/environment';
import { initLogging, setLoggingContext } from './logger';

initLogging();

redirectFromUnauthorizedOrAuthenticationError();

if (environment.production) {
  enableProdMode();
}

let preferredOdpOrgId = getPreferredOdpOrgId();

// Used for Internal environment only
if (environment.dummyAuth) {
  document.cookie = `GsId={"userId":54321,"odpOrgId":${preferredOdpOrgId}};domain=.int.test.visma.net;path=/`;
}

fetch(
  `${environment.dataImportApiBaseUrl}/session${
    preferredOdpOrgId ? '?org=' + preferredOdpOrgId : ''
  }`,
  {
    credentials: 'include',
    headers: {
      [CallCorrelationIdHeaderName]: vismaCallId(),
      [SessionCorrelationIdHeaderName]: vismaSessionId(),
    },
  },
)
  .then((res) => {
    if ([401, 403].includes(res.status)) {
      redirectToLoginPage(environment.signinUrl, environment.loginRedirectUrl);
    } else if (res.status === 503) {
      console.warn(`Service unavailable.`);
      redirectToServiceUnavailablePage();
    } else if (res.status !== 200) {
      console.error(`Authorization failed with [${res.status}] status.`);
      redirectToAuthenticationErrorPage();
    }
    return res;
  })
  .then((userSessionResponse) => {
    bootstrapApp(userSessionResponse);
  })
  .catch((err) => {
    console.error(err);
    redirectToAuthenticationErrorPage();
    return err;
  });

async function bootstrapApp(userSessionResponse) {
  const session =
    userSessionResponse?.status === 200
      ? new SessionDto(await userSessionResponse.json().catch((err) => console.error(err)))
      : undefined;

  const userLanguage = getLocalePreference(session?.languageCode);
  const locale = await import(`./locale/messages.${userLanguage}.json`);

  preferredOdpOrgId = preferredOdpOrgId || getPreferredOdpOrgId(session);

  setLoggingContext(preferredOdpOrgId, session);

  removeQueryParam('contextSelector:switchContext');

  if (session && preferredOdpOrgId === '') {
    console.error('User does not have access to any contexts.');
    redirectToUnauthorizedPage();
  } else if (
    session &&
    !session.availableContexts?.some((context) => context.id.toString() === preferredOdpOrgId)
  ) {
    console.error(`User does not have access to company with OdpOrgId ${preferredOdpOrgId}.`);
    redirectToUnauthorizedPage();
  }

  loadTranslations(locale.translations || locale.default.translations);

  let version = getVersionFromUrl();
  if (!version && environment.dev) {
    // Always include version (branch) in URL on dev domain.
    // On other domains, only include version if already present in URL.
    version = getVersionFromBaseUri();
  }
  const baseHref = (version ? `/v/${version}` : '') + `/${preferredOdpOrgId}`;

  platformBrowserDynamic([
    {
      provide: APP_BASE_HREF,
      useFactory: () => baseHref,
    },
    {
      provide: ORG_ID,
      useFactory: () => preferredOdpOrgId,
    },
    {
      provide: USER_SESSION,
      useValue: session,
    },
    { provide: LOCALE_ID, useValue: getLocalePreference() },
  ])
    .bootstrapModule(AppModule, {
      applicationProviders: [provideZoneChangeDetection()],
    })
    .catch((err) => console.error(err));

  /*
   * esbuild does not support "magic" dynamic imports: https://github.com/evanw/esbuild/issues/1240
   * but only for a few locale imports, that's not a huge impact on the bundle size
   */
  switch (getAngularFileLocaleName()) {
    case 'en-GB':
      registerLocaleData(enGbLocale);
      break;
    case 'nb':
      // nb-NO does not exist in Angular - only nb is used
      registerLocaleData(nbLocale);
      break;
  }
}

/**
 * Redirects to the login page with the current context and requested page.
 */
function redirectToLoginPage(
  signinUrl: string,
  loginRedirectUrl: string,
  session?: SessionDto,
): void {
  const navigationPath = getPathWithPreferredOrgId(session);
  const requestedPage = loginRedirectUrl + navigationPath;

  window.location.href =
    `${signinUrl}` + `?msgCode=NOTLOGGEDIN` + `&requestedPage=${encodeURIComponent(requestedPage)}`;
}

function redirectToAuthenticationErrorPage(): void {
  window.history.replaceState(null, '', `/authentication-error`);
}

function redirectToUnauthorizedPage(): void {
  window.history.replaceState(null, '', `/unauthorized`);
}

function redirectToServiceUnavailablePage(): void {
  window.history.replaceState(null, '', `/service-unavailable`);
}

function redirectFromUnauthorizedOrAuthenticationError(): void {
  const pathnameSplits = window.location.pathname.split('/');
  const popped = pathnameSplits.pop();

  if (popped && ['unauthorized', 'authentication-error', 'segment-error'].includes(popped)) {
    window.history.replaceState(null, '', pathnameSplits.join('/'));
  }
}

function removeQueryParam(param: string): void {
  const url = new URL(location.href);
  url.searchParams.delete(param);
  history.replaceState(null, '', url);
}
